import { z } from "zod";
import {
  customCotationManuelPickupFormSchema,
  customCotationPickupLocationIdFormSchema,
  parcelBottleProductSchema,
  parcelOtherProductSchema,
} from "@/types/forms/home/customCotationsForm.form";
import {
  customCotationMultipleDeliveryFormSchema,
  customCotationSingleDeliveryFormSchema,
} from "@/types/forms/home/customCotationsOtherForm.form";
import { OtherCustomQuotationTypeEnum } from "@/constants/enums/otherCustomQuotationType.enum";
import { i18nMessage } from "@/lib/i18nMessage";

const invoiceDataSchema = z.object({
  client: z.string({
    required_error: i18nMessage("boQuotations:quoteForm.clientRequired"),
    message: i18nMessage("boQuotations:quoteForm.clientRequired"),
  }),
  customId: z.string({
    required_error: i18nMessage("boQuotations:quoteForm.destinationCountryRequired"),
    message: i18nMessage("boQuotations:quoteForm.destinationCountryRequired"),
  }),
  reference: z.string().optional(),
  transportHt: z
    .number({
      required_error: i18nMessage("boQuotations:quoteForm.transportPriceRequired"),
      message: i18nMessage("boQuotations:quoteForm.transportPriceRequired"),
    })
    .min(0, i18nMessage("boQuotations:quoteForm.transportPriceRequired")),
  packaging: z
    .number({
      required_error: i18nMessage("boQuotations:quoteForm.packagingPriceRequired"),
      message: i18nMessage("boQuotations:quoteForm.packagingPriceRequired"),
    })
    .min(0, i18nMessage("boQuotations:quoteForm.packagingPriceRequired")),
  packagingProtection: z
    .number({
      required_error: i18nMessage("boQuotations:quoteForm.protectionPriceRequired"),
      message: i18nMessage("boQuotations:quoteForm.protectionPriceRequired"),
    })
    .min(0, i18nMessage("boQuotations:quoteForm.protectionPriceRequired")),
  taxes: z
    .number({
      required_error: i18nMessage("boQuotations:quoteForm.taxesAmountRequired"),
      message: i18nMessage("boQuotations:quoteForm.taxesAmountRequired"),
    })
    .min(0, i18nMessage("boQuotations:quoteForm.taxesAmountRequired")),
  insurance: z
    .number({
      required_error: i18nMessage("boQuotations:quoteForm.insurancePriceRequired"),
      message: i18nMessage("boQuotations:quoteForm.insurancePriceRequired"),
    })
    .min(0, i18nMessage("boQuotations:quoteForm.insurancePriceRequired")),
  tva: z
    .number({
      required_error: i18nMessage("boQuotations:quoteForm.vatAmountRequired"),
      message: i18nMessage("boQuotations:quoteForm.vatAmountRequired"),
    })
    .min(0, i18nMessage("boQuotations:quoteForm.vatAmountRequired")),
  merchandiseValue: z
    .number({
      required_error: i18nMessage("boQuotations:quoteForm.merchandiseValueRequired"),
      message: i18nMessage("boQuotations:quoteForm.merchandiseValueRequired"),
    })
    .min(0, i18nMessage("boQuotations:quoteForm.merchandiseValueRequired")),
  insuranceTvaOverride: z.boolean().optional(),
});

export const CustomCotationQuoteSchema = z.object({
  invoiceData: z
    .array(invoiceDataSchema)
    .min(1, i18nMessage("boQuotations:quoteForm.quoteRequired")),
});

export type CustomCotationQuoteFormData = z.infer<typeof CustomCotationQuoteSchema>;

export const UpdateCustomQuotationSchema = z.object({
  trackingUrl: z.string().url().optional(),
  trackingCode: z
    .string()
    .regex(/^\d+$/, {
      message: i18nMessage("boQuotations:quoteForm.trackingCodeDigitsOnly"),
    })
    .optional(),
  pickupInfo: z.discriminatedUnion("manualPickup", [
    customCotationManuelPickupFormSchema,
    customCotationPickupLocationIdFormSchema,
  ]),
  deliveryInfo: z.discriminatedUnion("singleDelivery", [
    customCotationSingleDeliveryFormSchema,
    customCotationMultipleDeliveryFormSchema,
  ]),
  billingEntityId: z
    .string({
      message: i18nMessage("boQuotations:quoteForm.billingEntityRequired"),
    })
    .min(1, i18nMessage("boQuotations:quoteForm.billingEntityRequired")),
  insurance: z.boolean(),
  express: z.boolean(),
  reference: z.string().optional(),
});

export type UpdateCustomQuotationFormData = z.infer<typeof UpdateCustomQuotationSchema>;

export const ReCustomQuotationSchema = z.object({
  invoiceData: invoiceDataSchema,
  pickupInfo: z.discriminatedUnion("manualPickup", [
    customCotationManuelPickupFormSchema,
    customCotationPickupLocationIdFormSchema,
  ]),
  deliveryInfo: z.discriminatedUnion("singleDelivery", [
    customCotationSingleDeliveryFormSchema,
    customCotationMultipleDeliveryFormSchema,
  ]),
  billingEntityId: z
    .string({
      message: i18nMessage("boQuotations:quoteForm.billingEntityRequired"),
    })
    .min(1, i18nMessage("boQuotations:quoteForm.billingEntityRequired")),
  insurance: z.boolean(),
  express: z.boolean(),
  reference: z.string().optional(),
  otherCustomQuotationType: z.nativeEnum(OtherCustomQuotationTypeEnum).optional(),
  parcelProducts: z
    .array(z.discriminatedUnion("type", [parcelBottleProductSchema, parcelOtherProductSchema]))
    .optional(),
  parcelPackages: z
    .array(
      z.object({
        length: z
          .number({
            coerce: true,
            message: i18nMessage("boQuotations:quoteForm.parcelLengthRequired"),
          })
          .min(1, i18nMessage("boQuotations:quoteForm.paletteLengthRequired")),
        width: z
          .number({
            coerce: true,
            message: i18nMessage("boQuotations:quoteForm.parcelWidthRequired"),
          })
          .min(1, i18nMessage("boQuotations:quoteForm.paletteWidthRequired")),
        height: z
          .number({
            coerce: true,
            message: i18nMessage("boQuotations:quoteForm.parcelHeightRequired"),
          })
          .min(0, i18nMessage("boQuotations:quoteForm.paletteHeightRequired")),
        weight: z
          .number({
            coerce: true,
            message: i18nMessage("boQuotations:quoteForm.parcelWeightRequired"),
          })
          .min(0, i18nMessage("boQuotations:quoteForm.paletteWeightRequired")),
        value: z
          .number({
            coerce: true,
            message: i18nMessage("boQuotations:quoteForm.parcelValueRequired"),
          })
          .min(0, i18nMessage("boQuotations:quoteForm.parcelValueRequired")),
      }),
    )
    .optional(),
  paletteProducts: z
    .array(
      z.object({
        volume: z
          .number({ coerce: true, message: i18nMessage("boQuotations:quoteForm.volumeRequired") })
          .gt(0, i18nMessage("boQuotations:quoteForm.bottleVolumeRequired")),
        bottleQuantity: z
          .number({
            coerce: true,
            message: i18nMessage("boQuotations:quoteForm.bottleQuantityRequired"),
          })
          .min(1, i18nMessage("boQuotations:quoteForm.bottleQuantityRequired")),
      }),
    )
    .optional(),
  palettePackages: z
    .array(
      z.object({
        length: z
          .number({
            coerce: true,
            message: i18nMessage("boQuotations:quoteForm.paletteLengthRequired"),
          })
          .min(1, i18nMessage("boQuotations:quoteForm.paletteLengthRequired")),
        width: z
          .number({
            coerce: true,
            message: i18nMessage("boQuotations:quoteForm.paletteWidthRequired"),
          })
          .min(1, i18nMessage("boQuotations:quoteForm.paletteWidthRequired")),
        height: z
          .number({
            coerce: true,
            message: i18nMessage("boQuotations:quoteForm.paletteHeightRequired"),
          })
          .min(0, i18nMessage("boQuotations:quoteForm.paletteHeightRequired")),
        weight: z
          .number({
            coerce: true,
            message: i18nMessage("boQuotations:quoteForm.paletteWeightRequired"),
          })
          .min(0, i18nMessage("boQuotations:quoteForm.paletteWeightRequired")),
        value: z
          .number({
            coerce: true,
            message: i18nMessage("boQuotations:quoteForm.paletteValueRequired"),
          })
          .min(0, i18nMessage("boQuotations:quoteForm.paletteValueRequired")),
      }),
    )
    .optional(),
  basedOnId: z.string(),
  skipPickup: z.boolean({
    required_error: i18nMessage("boQuotations:quoteForm.skipPickupRequired"),
    invalid_type_error: i18nMessage("boQuotations:quoteForm.skipPickupRequired"),
  }),
  customId: z
    .string({
      message: i18nMessage("boQuotations:quoteForm.destinationRequired"),
    })
    .min(1, i18nMessage("boQuotations:quoteForm.destinationRequired")),
  note: z
    .string({ message: i18nMessage("boQuotations:quoteForm.noteRequired") })
    .min(1, { message: i18nMessage("boQuotations:quoteForm.noteRequired") }),
  transportDiscountPercentage: z.number().min(0).max(100).optional().default(0),
  overallDiscountPercentage: z.number().min(0).max(100).optional().default(0),
});

export type ReCustomQuotationFormData = z.infer<typeof ReCustomQuotationSchema>;
